import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Clock, Repeat, DollarSign, Zap, Crown, Ticket, MousePointer2, UserPlus, 
  RotateCcw, ArrowRight, MessageSquare, ShoppingCart, Activity, 
  Send, Users, RefreshCw, Inbox
} from 'lucide-react';
import '../segments/Segments.css';
import { getApiUrl } from '../../config/api';


const API_URL = getApiUrl('/analytics/segmentation-hub');
const getToken = () => localStorage.getItem('access_token') || '';


const ICON_MAP = {
  'ready-to-buy': Zap, 'high intent': Zap,
  'vip': Crown, 'vips': Crown,
  'new lead': UserPlus, 'new leads': UserPlus,
  'returning': RotateCcw,
  'coupon': Ticket, 'coupon seekers': Ticket,
  'price': MessageSquare, 'price inquiry': MessageSquare,
  'cart': ShoppingCart, 'cartabandoners': ShoppingCart, 'abandon': ShoppingCart,
  'browser': MousePointer2, 'browsers': MousePointer2,
};


const getIconFor = (title = '') => {
  const key = title.toLowerCase();
  for (const k in ICON_MAP) {
    if (key.includes(k)) return ICON_MAP[k];
  }
  return Users;
};


// Formats a raw growth number (e.g. 12, -3, 0) into a display string like "+12%", "-3%", "0%"
const formatGrowth = (val) => {
  if (val === null || val === undefined) return null;
  const num = Number(val);
  if (num > 0) return `+${num}%`;
  if (num < 0) return `${num}%`;
  return '0%';
};


// Formats a raw count number (e.g. 3450) into "3,450"
const formatCount = (val) => {
  if (val === null || val === undefined) return '0';
  if (typeof val === 'string') return val;
  return Number(val).toLocaleString();
};


// Maps AI recommendation conversion_estimate ("High"/"Medium"/"Low") to a card accent color
const mapConversionColor = (estimate) => {
  const key = (estimate || '').toLowerCase();
  if (key === 'high') return 'green';
  if (key === 'medium') return 'orange';
  if (key === 'low') return 'gray';
  return 'gray';
};


const RFM_META = [
  { key: 'recency', title: 'RECENCY', unit: 'days avg', icon: Clock, labels: ['Active', 'At Risk'], colors: ['emerald', 'gray'], pctKeys: ['active_pct', 'at_risk_pct'] },
  { key: 'frequency', title: 'FREQUENCY', unit: 'orders/yr', icon: Repeat, labels: ['Loyal', 'One-time'], colors: ['purple', 'blue'], pctKeys: ['loyal_pct', 'one_time_pct'] },
  { key: 'monetary', title: 'MONETARY (LTV)', unit: 'avg/user', icon: DollarSign, labels: ['Whales', 'Mid-Tier'], colors: ['orange', 'orange-dark'], pctKeys: ['whales_pct', 'mid_tier_pct'] },
];


const Segments = () => {
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  const fetchSegments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const res = await fetch(API_URL, {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const json = await res.json();
      setData(json);
      if (Array.isArray(json?.smart_behaviors) && json.smart_behaviors.length > 0) {
        setSelectedSegment(json.smart_behaviors[0].id || json.smart_behaviors[0].name);
      }
    } catch (err) {
      console.error('Segments fetch error:', err);
      setError('Unable to load segmentation data from the server.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchSegments();
  }, [fetchSegments]);


  const rfm = data?.rfm || {};

  // Maps the new schema's { id, name, count, growth } shape into the card view model
  const behaviorSegments = (Array.isArray(data?.smart_behaviors) ? data.smart_behaviors : []).map((seg, idx) => {
    const name = seg.name || seg.id || `Segment ${idx + 1}`;
    const trend = formatGrowth(seg.growth);
    return {
      id: seg.id || name || `segment-${idx}`,
      icon: getIconFor(name),
      label: seg.label, // not present in new schema; badge simply won't render
      title: name,
      subtitle: seg.subtitle || '', // not present in new schema
      value: formatCount(seg.count),
      trend,
      ctr: seg.ctr,
      aov: seg.aov,
    };
  });


  // Maps the new schema's { title, action, conversion_estimate } shape into the action card view model
  const aiActions = (Array.isArray(data?.ai_recommended_actions) ? data.ai_recommended_actions : []).map((item) => ({
    tag: item.title || 'Suggested',
    title: item.action || 'No action specified',
    conversionEstimate: item.conversion_estimate,
    color: mapConversionColor(item.conversion_estimate),
  }));


  const rfmData = RFM_META.map(meta => ({
    title: meta.title,
    value: rfm[meta.key]?.avg ?? 0,
    unit: meta.unit,
    icon: meta.icon,
    segments: meta.labels.map((label, i) => ({
      label,
      percent: rfm[meta.key]?.[meta.pctKeys[i]] ?? 0,
      color: meta.colors[i],
    })),
  }));


  const currentSegmentData = behaviorSegments.find(s => s.id === selectedSegment) || behaviorSegments[0] || null;
  const hasBehaviorData = behaviorSegments.length > 0;


  return (
    <>
      <Helmet>
        <title>Segmentation Hub - Revexia AI Revenue Brain</title>
        <meta name="description" content="Smart behavior segments and RFM analysis" />
      </Helmet>


      <div className="segments-page-wrapper">
        <div className="container-fluid px-4">


          <div className="d-flex justify-content-between align-items-center mb-3 segments-toolbar">
            {error && (
              <div className="fallback-notice">
                <Inbox size={14} />
                <span>{error}</span>
              </div>
            )}
            <button
              className="attribution-refresh-btn ms-auto"
              onClick={fetchSegments}
              disabled={loading}
              aria-label="Refresh segmentation data"
            >
              <RefreshCw size={16} className={loading ? 'spin' : ''} />
              <span>Refresh</span>
            </button>
          </div>


          <div className="row g-4">
            {/* Main Content Area */}
            <div className="col-12 col-xl-8">
              <div className="segments-main-content">
                {/* RFM Analysis Section */}
                <section className="segments-section">
                  <div className="section-header-with-icon">
                    <Activity size={20} className="section-icon emerald" />
                    <h2 className="section-main-title">RFM Analysis</h2>
                  </div>
                  <div className="row g-4">
                    {rfmData.map((rfmItem, index) => (
                      <div key={index} className="col-12 col-md-4">
                        <div className="rfm-card">
                          <div className="rfm-card-header">
                            <div className="rfm-card-info">
                              <p className="rfm-card-label">{rfmItem.title}</p>
                              <div className="rfm-card-value-wrapper">
                                <span className="rfm-card-value">{loading ? '—' : rfmItem.value}</span>
                                <span className="rfm-card-unit">{rfmItem.unit}</span>
                              </div>
                            </div>
                            <div className="rfm-card-icon">
                              <rfmItem.icon size={24} />
                            </div>
                          </div>
                          <div className="rfm-segments-list">
                            {rfmItem.segments.map((seg, i) => (
                              <div key={i} className="rfm-segment-item">
                                <div className="rfm-segment-header">
                                  <span className="rfm-segment-label">{seg.label}</span>
                                  <span className="rfm-segment-percent">{seg.percent}%</span>
                                </div>
                                <div className="rfm-segment-progress-track">
                                  <div
                                    className={`rfm-segment-progress-bar progress-${seg.color}`}
                                    style={{ width: `${Math.min(Math.max(seg.percent, 0), 100)}%` }}
                                  ></div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>


                {/* Behavior Segments Section */}
                <section className="segments-section">
                  <div className="section-header-with-status">
                    <div className="section-header-with-icon">
                      <Users size={20} className="section-icon cyan" />
                      <h2 className="section-main-title">Smart Behavior Segments</h2>
                    </div>
                    <div className="live-updates-badge">
                      <span className="live-pulse-dot"></span>
                      <span className="live-text">Live Updates</span>
                    </div>
                  </div>


                  {loading ? (
                    <div className="attribution-state-card">
                      <div className="loading-spinner"></div>
                      <p>Loading segments...</p>
                    </div>
                  ) : !hasBehaviorData ? (
                    <div className="no-data-block">
                      <Inbox size={26} />
                      <p>No behavior segments available yet</p>
                    </div>
                  ) : (
                    <div className="row g-4">
                      {behaviorSegments.map((seg) => (
                        <div key={seg.id} className="col-12 col-md-6 col-lg-4">
                          <div
                            onClick={() => setSelectedSegment(seg.id)}
                            className={`behavior-segment-card ${selectedSegment === seg.id ? 'active' : ''}`}
                          >
                            <div className="behavior-card-header">
                              <div className="behavior-card-icon">
                                <seg.icon size={20} />
                              </div>
                              {seg.label && (
                                <span className="behavior-card-badge">{seg.label}</span>
                              )}
                            </div>
                            <div className="behavior-card-body">
                              <h4 className="behavior-card-title">{seg.title}</h4>
                              <p className="behavior-card-subtitle">{seg.subtitle}</p>
                              <div className="behavior-card-footer">
                                <span className="behavior-card-value">{seg.value}</span>
                                {seg.trend && (
                                  <span className={`behavior-card-trend ${
                                    seg.trend.startsWith('+') ? 'positive' :
                                    seg.trend === '0%' || seg.trend === '— 0%' ? 'neutral' :
                                    'negative'
                                  }`}>
                                    {seg.trend}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>
            </div>


            {/* Right Context Sidebar */}
            <div className="col-12 col-xl-4">
              <div className="context-sidebar">
                {!currentSegmentData ? (
                  <div className="no-data-block">
                    <Inbox size={26} />
                    <p>Select a segment to view analysis</p>
                  </div>
                ) : (
                  <>
                    {/* Segment Header */}
                    <div className="context-sidebar-header">
                      <div className="context-icon-wrapper">
                        <currentSegmentData.icon size={28} />
                      </div>
                      <div className="context-header-info">
                        <p className="context-header-label">Context Analysis</p>
                        <h3 className="context-header-title">{currentSegmentData.title}</h3>
                      </div>
                    </div>


                    {/* Population Stats */}
                    <div className="context-stats-section">
                      <div className="context-population">
                        <div className="population-info">
                          <p className="population-label">Population</p>
                          <p className="population-value">{currentSegmentData.value}</p>
                        </div>
                        {Array.isArray(currentSegmentData?.history) && currentSegmentData.history.length > 0 ? (
                          <div className="population-chart">
                            {currentSegmentData.history.map((h, i) => (
                              <div
                                key={i}
                                className={`population-bar ${i === currentSegmentData.history.length - 1 ? 'active' : ''}`}
                                style={{ height: `${typeof h === 'number' ? h : h.value || 0}%` }}
                              ></div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="context-metric-card">
                            <p className="context-metric-label">CTR</p>
                            <p className="context-metric-value emerald">
                              {currentSegmentData.ctr ?? '—'}
                            </p>
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="context-metric-card">
                            <p className="context-metric-label">AOV</p>
                            <p className="context-metric-value">
                              {currentSegmentData.aov ?? '—'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}


                {/* Strategic Next Steps */}
                <div className="context-actions-section">
                  <h4 className="context-section-title">Strategic Next Steps</h4>
                  {aiActions.length === 0 ? (
                    <div className="no-data-block compact">
                      <Inbox size={20} />
                      <p>No AI actions recommended yet</p>
                    </div>
                  ) : (
                    <div className="context-actions-list">
                      {aiActions.map((step, i) => (
                        <div key={i} className={`context-action-card action-${step.color || 'gray'}`}>
                          <div className="context-action-header">
                            <span className="context-action-tag">{step.tag || 'Suggested'}</span>
                            <ArrowRight size={14} className="context-action-arrow" />
                          </div>
                          <p className="context-action-title">{step.title}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>


                {/* Engage Button */}
                <div className="context-engage-section">
                  <button className="engage-segment-btn" disabled={!currentSegmentData}>
                    <Send size={18} />
                    <span>Engage Segment</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};


export default Segments;